import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToolConfigService, type ToolConfig, type ToolConfigUpdate } from '../services/toolConfigService';

export type ToolConfigMap = Record<string, ToolConfig>;

export const toolsetKeys = {
  configs: () => ['toolset', 'configs'] as const,
};

// The persisted admin overlay over the code-defined tool catalog. Best-effort:
// on error the map is empty and the catalog's code defaults render unchanged.
export function useToolConfigs(enabled: boolean) {
  const service = useToolConfigService();

  return useQuery({
    queryKey: toolsetKeys.configs(),
    queryFn: async () => {
      const list = await service!.listToolConfigs();
      return Object.fromEntries(list.map((c) => [c.action_id, c])) as ToolConfigMap;
    },
    enabled: enabled && !!service,
  });
}

export function useUpsertToolConfig() {
  const service = useToolConfigService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ actionId, update }: { actionId: string; update: ToolConfigUpdate }) =>
      service!.upsertToolConfig(actionId, update),
    onSuccess: (saved) => {
      queryClient.setQueryData<ToolConfigMap>(toolsetKeys.configs(), (prev) => ({
        ...(prev ?? {}),
        [saved.action_id]: saved,
      }));
    },
  });
}
